import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowRight, Zap, Mail, MessageSquare } from 'lucide-react';
import { useCacxiStripe } from '../../../hooks/dashboard/useStripe';

export interface CreditPackage {
  id: string;
  credits: number;
  tokens: string;
  sms: number;
  emails: number;
  price: number;
  discountedPrice?: number;
}

interface CreditPackagesDialogProps {
  open: boolean;
  onClose: () => void;
  stripeCustomerId?: string; // Optional subscription ID if needed
}

const CreditPackagesDialog: React.FC<CreditPackagesDialogProps> = ({
  open,
  onClose,
  stripeCustomerId,
}) => {
  const theme = useTheme();
  const {
    oneOfPlans,
    buyCredits,
  }: {
    oneOfPlans: {
      id: string;
      product: { name: string; description: string };
      unit_amount: number;
    }[];
    buyCredits: (priceId: string, stripeCustomerId?: string) => Promise<string | null>;
  } = useCacxiStripe();
  const [loadingPackageId, setLoadingPackageId] = useState<string | null>(null);

  const packages: CreditPackage[] = oneOfPlans
    .map(plan => {
      const name = plan.product.name.toLowerCase();

      if (name.includes('growth')) {
        return {
          id: plan.id,
          credits: 250,
          tokens: '~1250 Chats (2,500,000 tokens)',
          sms: 750,
          emails: 87500,
          price: plan.unit_amount / 100,
          discountedPrice: 29.0,
        };
      }

      if (name.includes('scale')) {
        return {
          id: plan.id,
          credits: 500,
          tokens: '~2500 Chats (5,000,000 tokens)',
          sms: 1500,
          emails: 175000,
          price: plan.unit_amount / 100,
          discountedPrice: 49.0,
        };
      }

      return null;
    })
    .filter(Boolean) as CreditPackage[];

  const handlePurchase = async (pkg: CreditPackage) => {
    try {
      setLoadingPackageId(pkg.id);
      const response = await buyCredits(pkg.id, stripeCustomerId);
      console.log('Purchase initiated:', response);
      if (response) {
        // Redirect to Stripe checkout
        window.location.href = response;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to initiate credit purchase:', error);
    } finally {
      setLoadingPackageId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Additional Credit Packages
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Purchase additional credits in larger packages for better value
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            {packages.map(pkg => (
              <Grid item xs={12} md={6} key={pkg.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                        {pkg.credits} Credits
                      </Typography>
                      {pkg.discountedPrice && (
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="body1"
                            sx={{
                              textDecoration: 'line-through',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            AUD {pkg.price.toFixed(2)}
                          </Typography>
                          <Typography variant="h6" sx={{ color: theme.palette.success.main }}>
                            AUD {pkg.discountedPrice.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Zap
                          size={18}
                          style={{ marginRight: 8, color: theme.palette.warning.main }}
                        />
                        <Typography variant="body2">{pkg.tokens}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <MessageSquare
                          size={18}
                          style={{ marginRight: 8, color: theme.palette.info.main }}
                        />
                        <Typography variant="body2">{pkg.sms.toLocaleString()} SMS</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Mail
                          size={18}
                          style={{ marginRight: 8, color: theme.palette.primary.main }}
                        />
                        <Typography variant="body2">
                          {pkg.emails.toLocaleString()} emails
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary">
                      * Credits can be used across any of our services. All purchased credits are
                      valid for 12 months from the date of purchase.
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowRight size={16} />}
                      onClick={() => handlePurchase(pkg)}
                      disabled={loadingPackageId === pkg.id}
                      sx={{
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        py: 1.5,
                      }}
                    >
                      {loadingPackageId === pkg.id ? 'Redirecting…' : 'Purchase Package'}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditPackagesDialog;
